import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Divider,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete'; // 👈 Icône pour désaffecter
import axios from 'axios';
import { toast } from 'react-toastify';

const ViewAidantDialog = ({ open, aidantId, onClose, apiUrl, token, onPatientDetached }) => {
  const [aidant, setAidant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fonction pour charger les détails de l'aidant
  const fetchAidantDetails = async () => {
    if (!aidantId) return;
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/admin/aidants/${aidantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data.data || response.data;
      setAidant(data);
    } catch (err) {
      console.error('Erreur chargement détails aidant :', err);
      toast.error('Erreur lors du chargement des détails de l’aidant');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && aidantId) {
      fetchAidantDetails();
    } else {
      setAidant(null);
    }
  }, [open, aidantId, apiUrl, token]);

  // Fonction pour désaffecter un patient
  const handleDetachPatient = async (patientId, patientName) => {
    if (window.confirm(`Voulez-vous vraiment désaffecter le patient ${patientName} de cet aidant ?`)) {
      try {
        await axios.delete(`${apiUrl}/admin/aidants/${aidantId}/patients/${patientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Patient désaffecté avec succès');
        
        // Rafraîchir les détails de l'aidant localement
        fetchAidantDetails();

        // Si le parent a besoin de se mettre à jour (ex: pour le tableau principal)
        if (onPatientDetached) onPatientDetached();
      } catch (err) {
        console.error('Erreur lors de la désaffectation :', err);
        toast.error('Erreur lors de la désaffectation du patient');
      }
    }
  };

  // Fonction pour formater joliment les dates ISO
  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? dateString : date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${apiUrl.replace('/api', '')}/storage/${photoPath}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', color: '#2B3674' }}>
        Détails de l'Aidant
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#4318FF' }} />
          </Box>
        ) : aidant ? (
          <Stack spacing={2}>
            {/* En-tête avec Photo et Nom */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar
                src={getPhotoUrl(aidant.photo_aidant)}
                alt={`${aidant.nom_aidant} ${aidant.prenom_aidant}`}
                sx={{ width: 64, height: 64, bgcolor: '#4318FF', fontSize: '1.5rem' }}
              >
                {aidant.nom_aidant?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2B3674' }}>
                  {aidant.nom_aidant} {aidant.prenom_aidant}
                </Typography>
                <Chip
                  label={aidant.actif_aidant ? 'Actif' : 'Inactif'}
                  color={aidant.actif_aidant ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Informations détaillées */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="textSecondary">ID Aidant</Typography>
                <Typography variant="body1" fontWeight={500}>{aidant.id_aidant}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Client ID</Typography>
                <Typography variant="body1" fontWeight={500}>{aidant.client_id || 'Aucun'}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">Email</Typography>
                <Typography variant="body1" fontWeight={500}>{aidant.email_aidant || 'Non renseigné'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Téléphone</Typography>
                <Typography variant="body1" fontWeight={500}>{aidant.telephone_aidant || 'Non renseigné'}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">Ville</Typography>
                <Typography variant="body1" fontWeight={500}>{aidant.ville_aidant || 'Non renseignée'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Profession</Typography>
                <Typography variant="body1" fontWeight={500}>{aidant.profession_aidant || 'Non renseignée'}</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="textSecondary">Adresse complète</Typography>
              <Typography variant="body1" fontWeight={500}>{aidant.adresse_aidant || 'Non renseignée'}</Typography>
            </Box>

            <Divider />

            {/* Section Patients Rattachés avec possibilité de désaffecter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2B3674', mb: 1 }}>
                Patients Rattachés ({aidant.patients ? aidant.patients.length : 0})
              </Typography>
              {aidant.patients && aidant.patients.length > 0 ? (
                <Stack spacing={1}>
                  {aidant.patients.map((patient, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#FAFCFE',
                        p: 1.5,
                        borderRadius: '8px',
                        border: '1px solid #E0E5F2',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PersonIcon sx={{ color: '#4318FF' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#2B3674">
                            {patient.nom_patient} {patient.prenom_patient}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {patient.id_patient_profile} | Tél: {patient.telephone_patient || 'Non renseigné'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={patient.pivot?.lien_patient_aidant || 'Lien non défini'}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.75rem' }}
                        />
                        <Tooltip title="Désaffecter ce patient">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDetachPatient(patient.id_patient_profile, `${patient.nom_patient} ${patient.prenom_patient}`)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  Aucun patient rattaché à cet aidant.
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Section Dates de création et mise à jour */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, bgcolor: '#F4F7FE', p: 2, borderRadius: '12px' }}>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Créé le</Typography>
                <Typography variant="body2" fontWeight={600} color="#2B3674">
                  {formatDate(aidant.created_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Dernière modification</Typography>
                <Typography variant="body2" fontWeight={600} color="#2B3674">
                  {formatDate(aidant.updated_at)}
                </Typography>
              </Box>
            </Box>

          </Stack>
        ) : (
          <Typography align="center" color="textSecondary">Aucune information trouvée.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#4318FF', '&:hover': { bgcolor: '#3311CC' } }}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAidantDialog;